import React, { useMemo } from 'react'
import LinkIcon from '@mui/icons-material/Link'
import RouteIcon from '@mui/icons-material/Route'
import { styled } from '@mui/material'
import { DatePosition, getPercentXForDate } from '../hooks/useDatePositions'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { GanttColors } from '../constants'
import { Dependency } from '../GanttDependency/DependencyContext'
import { CriticalPathLinkData } from '../GanttCriticalPath/CriticalPathContext'
import { FallbackLinkInfo } from '../utils/visibilityFilters'
import { colorPalette } from '../../../../../../../../../style/colorPalette'
import { ACTUAL_BAR_SET_SHIFT } from '../../ganttUtil'

const ICON_SIZE = 10
const CIRCLE_SIZE = 16
const BADGE_SIZE = 14
const GROUPING_THRESHOLD_PERCENT = 2

const getBarEdgePositions = (isSummaryRow: boolean) => {
  const setShift = isSummaryRow ? 0 : ACTUAL_BAR_SET_SHIFT
  const barHalfHeight = isSummaryRow ? 16 : 10
  return {
    top: `calc(50% - ${setShift + barHalfHeight}px)`,
    bottom: `calc(50% - ${setShift - barHalfHeight}px)`,
  }
}

type FallbackLinkIndicatorProps = {
  datePositions: DatePosition[]
  linearDatePositions: DatePosition[]
  fallbackDeps: FallbackLinkInfo<Dependency>[]
  fallbackCPs: FallbackLinkInfo<CriticalPathLinkData>[]
  currentRowIndex: number
  isSummaryRow: boolean
  onDepClick: (
    fallbackInfo: FallbackLinkInfo<Dependency>,
    event: React.MouseEvent
  ) => void
  onCpClick: (
    fallbackInfo: FallbackLinkInfo<CriticalPathLinkData>,
    event: React.MouseEvent
  ) => void
  onDepGroupClick?: (
    infos: FallbackLinkInfo<Dependency>[],
    event: React.MouseEvent
  ) => void
  onCpGroupClick?: (
    infos: FallbackLinkInfo<CriticalPathLinkData>[],
    event: React.MouseEvent
  ) => void
}

type IconPlacement = {
  key: string
  percentX: number
  topPosition: string
  color: string
  type: 'dependency' | 'criticalPath'
  onClick: (event: React.MouseEvent) => void
  depInfo?: FallbackLinkInfo<Dependency>
  cpInfo?: FallbackLinkInfo<CriticalPathLinkData>
}

type GroupedIcon = {
  key: string
  percentX: number
  topPosition: string
  color: string
  type: 'dependency' | 'criticalPath'
  count: number
  onClick: (event: React.MouseEvent) => void
}

/**
 * Group icons that overlap by (type, topPosition) and proximate percentX.
 * Icons within GROUPING_THRESHOLD_PERCENT of each other are merged into one.
 */
const groupIcons = (
  icons: IconPlacement[],
  onDepGroupClick:
    | ((infos: FallbackLinkInfo<Dependency>[], event: React.MouseEvent) => void)
    | undefined,
  onCpGroupClick:
    | ((
        infos: FallbackLinkInfo<CriticalPathLinkData>[],
        event: React.MouseEvent
      ) => void)
    | undefined
): GroupedIcon[] => {
  // Classify by (type, topPosition)
  const buckets = new Map<string, IconPlacement[]>()
  for (const icon of icons) {
    const bucketKey = `${icon.type}::${icon.topPosition}`
    const bucket = buckets.get(bucketKey)
    if (bucket) {
      bucket.push(icon)
    } else {
      buckets.set(bucketKey, [icon])
    }
  }

  const result: GroupedIcon[] = []

  for (const bucket of buckets.values()) {
    // Sort by percentX for proximity grouping
    bucket.sort((a, b) => a.percentX - b.percentX)

    let groupStart = 0
    for (let i = 1; i <= bucket.length; i++) {
      const isEnd =
        i === bucket.length ||
        bucket[i].percentX - bucket[i - 1].percentX > GROUPING_THRESHOLD_PERCENT

      if (isEnd) {
        const group = bucket.slice(groupStart, i)
        if (group.length === 1) {
          // Single icon: keep original click handler
          const icon = group[0]
          result.push({
            key: icon.key,
            percentX: icon.percentX,
            topPosition: icon.topPosition,
            color: icon.color,
            type: icon.type,
            count: 1,
            onClick: icon.onClick,
          })
        } else {
          // Multiple icons: create grouped representative
          const avgX =
            group.reduce((sum, g) => sum + g.percentX, 0) / group.length
          const representative = group[0]
          const depInfos = group
            .map(g => g.depInfo)
            .filter((d): d is FallbackLinkInfo<Dependency> => d !== undefined)
          const cpInfos = group
            .map(g => g.cpInfo)
            .filter(
              (c): c is FallbackLinkInfo<CriticalPathLinkData> =>
                c !== undefined
            )

          const groupClickHandler = (event: React.MouseEvent) => {
            if (representative.type === 'dependency' && onDepGroupClick) {
              onDepGroupClick(depInfos, event)
            } else if (
              representative.type === 'criticalPath' &&
              onCpGroupClick
            ) {
              onCpGroupClick(cpInfos, event)
            }
          }

          result.push({
            key: group.map(g => g.key).join('+'),
            percentX: avgX,
            topPosition: representative.topPosition,
            color: representative.color,
            type: representative.type,
            count: group.length,
            onClick: groupClickHandler,
          })
        }
        groupStart = i
      }
    }
  }

  return result
}

export const FallbackLinkIndicator = React.memo<FallbackLinkIndicatorProps>(
  ({
    datePositions,
    linearDatePositions,
    fallbackDeps,
    fallbackCPs,
    currentRowIndex,
    isSummaryRow,
    onDepClick,
    onCpClick,
    onDepGroupClick,
    onCpGroupClick,
  }) => {
    const groupedIcons = useMemo(() => {
      const rawIcons: IconPlacement[] = []
      const barEdge = getBarEdgePositions(isSummaryRow)
      const depSetShift = isSummaryRow ? 0 : ACTUAL_BAR_SET_SHIFT
      const depCenterTop = depSetShift ? `calc(50% - ${depSetShift}px)` : '50%'

      // Dependency fallback icons (positioned at bar center, matching DependencyLines)
      for (const info of fallbackDeps) {
        const { link, from, to } = info

        if (from.rowIndex === currentRowIndex && !from.isFallback) {
          // From-side icon: position at fromEndDate (right edge of bar)
          const date = link.fromEndDate
            ? CalendarDateVO.of(link.fromEndDate)
            : null
          if (date) {
            const x = getPercentXForDate(linearDatePositions, date, 'right')
            if (x !== null) {
              rawIcons.push({
                key: `dep-from-${link.uuid}`,
                percentX: x,
                topPosition: depCenterTop,
                color: link.color || GanttColors.DEPENDENCY_LINE,
                type: 'dependency',
                onClick: e => onDepClick(info, e),
                depInfo: info,
              })
            }
          }
        }

        if (to.rowIndex === currentRowIndex && !to.isFallback) {
          // To-side icon: position at toStartDate (left edge of bar)
          const date = link.toStartDate
            ? CalendarDateVO.of(link.toStartDate)
            : null
          if (date) {
            const x = getPercentXForDate(linearDatePositions, date, 'left')
            if (x !== null) {
              rawIcons.push({
                key: `dep-to-${link.uuid}`,
                percentX: x,
                topPosition: depCenterTop,
                color: link.color || GanttColors.DEPENDENCY_LINE,
                type: 'dependency',
                onClick: e => onDepClick(info, e),
                depInfo: info,
              })
            }
          }
        }
      }

      // Critical path fallback icons (positioned at bar edge, matching CriticalPathLines)
      for (const info of fallbackCPs) {
        const { link, from, to } = info

        if (from.rowIndex === currentRowIndex && !from.isFallback) {
          const x = getPercentXForDate(datePositions, link.fromDate, 'center')
          if (x !== null) {
            const isToBelow =
              to.isFallback || to.rowIndex < 0 || to.rowIndex >= currentRowIndex
            rawIcons.push({
              key: `cp-from-${link.uuid}`,
              percentX: x,
              topPosition: isToBelow ? barEdge.bottom : barEdge.top,
              color: link.color || GanttColors.CRITICAL_PATH_LINE,
              type: 'criticalPath',
              onClick: e => onCpClick(info, e),
              cpInfo: info,
            })
          }
        }

        if (to.rowIndex === currentRowIndex && !to.isFallback) {
          const x = getPercentXForDate(datePositions, link.toDate, 'center')
          if (x !== null) {
            const isFromAbove =
              from.isFallback ||
              from.rowIndex < 0 ||
              from.rowIndex <= currentRowIndex
            rawIcons.push({
              key: `cp-to-${link.uuid}`,
              percentX: x,
              topPosition: isFromAbove ? barEdge.top : barEdge.bottom,
              color: link.color || GanttColors.CRITICAL_PATH_LINE,
              type: 'criticalPath',
              onClick: e => onCpClick(info, e),
              cpInfo: info,
            })
          }
        }
      }

      return groupIcons(rawIcons, onDepGroupClick, onCpGroupClick)
    }, [
      datePositions,
      linearDatePositions,
      fallbackDeps,
      fallbackCPs,
      currentRowIndex,
      isSummaryRow,
      onDepClick,
      onCpClick,
      onDepGroupClick,
      onCpGroupClick,
    ])

    if (groupedIcons.length === 0) return null

    return (
      <>
        {groupedIcons.map(icon => (
          <div
            key={icon.key}
            style={{
              position: 'absolute',
              left: `${icon.percentX}%`,
              top: icon.topPosition,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'auto',
              cursor: 'pointer',
              zIndex: 5,
              width: CIRCLE_SIZE,
              height: CIRCLE_SIZE,
              borderRadius: '50%',
              backgroundColor: 'white',
              border: `1.5px solid ${icon.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={icon.onClick}
          >
            {icon.type === 'dependency' ? (
              <LinkIcon
                sx={{
                  fontSize: ICON_SIZE,
                  color: icon.color,
                }}
              />
            ) : (
              <RouteIcon
                sx={{
                  fontSize: ICON_SIZE,
                  color: icon.color,
                }}
              />
            )}
            {icon.count >= 2 && <CountBadge>{icon.count}</CountBadge>}
          </div>
        ))}
      </>
    )
  }
)

FallbackLinkIndicator.displayName = 'FallbackLinkIndicator'

const CountBadge = styled('span')({
  position: 'absolute',
  top: -BADGE_SIZE / 2,
  right: -BADGE_SIZE / 2,
  width: BADGE_SIZE,
  height: BADGE_SIZE,
  borderRadius: '50%',
  backgroundColor: colorPalette.pink[5],
  color: 'white',
  fontSize: 9,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  pointerEvents: 'none',
})
